import React, { useEffect, useRef, useState } from 'react';
import { TIPO_VISAO } from '~/modules/Helper/tipo-visao';
import { Input, Button, Space, Tooltip } from 'antd';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AA_TEXT_ON_LIGHT } from '~/modules/Helper/contrast.helper';
import {
  obterTipoLabelFiltro,
  obterDicaTipoFiltro,
} from '~/modules/Helper/filtroTipoCampoHelper';

const FilterHeaderTable = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  close,
  visaoColuna,
  setDataFilterHeader,
  dataFilterHeader,
  configuracao,
  obterColunaChave,
  obterColunaLink,
  setFiltro,
  filtro,
  setPagina
}) => {
  const searchInput = useRef(null);

  const [valorChave, setValorChave] = useState('');
  const [campoAlterado, setCampoAlterado] = useState(false);

  const { NomeColuna, FonteDados, TipoValor } = visaoColuna;

  let KeyVisao = null;
  if (configuracao && configuracao.VisaoColunas) {
    const visaoColuna =
      configuracao.Tipo === TIPO_VISAO.AGRUPAMENTO
        ? obterColunaChave()
        : obterColunaLink();

    KeyVisao = visaoColuna?.FonteDados;
  }

  const isKeyVisao = KeyVisao && KeyVisao == FonteDados;

  const obterValorAplicado = () => {
    if (isKeyVisao) {
      if (Array.isArray(filtro) && filtro.length > 0 && filtro[0]?.Campo == FonteDados) {
        return filtro[0]?.Valor != null ? String(filtro[0].Valor) : '';
      }
      return '';
    }

    const filtroColuna =
      dataFilterHeader?.Filtros?.find?.(x => x?.Campo == FonteDados) || null;

    return filtroColuna?.Valor != null ? String(filtroColuna.Valor) : '';
  };

  // Sincroniza o valor exibido do filtro "principal" (KeyVisao) com o estado real (filtro).
  // Importante quando o filtro é removido via painel externo: o antd mantém o dropdown,
  // mas o estado local `valorChave` precisa ser limpo para não "prender" o valor antigo.
  useEffect(() => {
    if (!isKeyVisao) return;
    if (campoAlterado) return;

    const aplicado = obterValorAplicado();
    if (aplicado !== valorChave) {
      setValorChave(aplicado);
    }
  }, [isKeyVisao, campoAlterado, filtro, FonteDados]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (selectedKeys, confirm, valorChave) => {
    confirm();

    // Se não há valor, trata como limpeza do filtro.
    if (!selectedKeys?.[0]) {
      if (isKeyVisao) {
        setFiltro([]);
        setValorChave('');
        setSelectedKeys(['']);
      } else {
        const next = (dataFilterHeader?.Filtros || []).filter(
          x => x?.Campo != FonteDados
        );
        setDataFilterHeader({ ...(dataFilterHeader || {}), Filtros: next });
      }

      setCampoAlterado(false);
      setPagina(1);
      return;
    }

    if (isKeyVisao) {
      setValorChave(valorChave);
      setFiltro([
        {
          Campo: FonteDados,
          Valor: selectedKeys[0],
          TipoValor,
        },
      ]);
      setSelectedKeys(['']);
    } else {
      let dataFilter = { ...dataFilterHeader };
      dataFilter.Filtros ? dataFilter.Filtros = dataFilter.Filtros.filter(x => x.Campo != FonteDados) : dataFilter.Filtros = [];
      dataFilter.Filtros.push({
        Campo: FonteDados,
        Valor: selectedKeys[0],
        TipoValor,
      });
      setDataFilterHeader(dataFilter);
    }
    setCampoAlterado(false);
    setPagina(1);
  };

  const handleReset = clearFilters => {
    clearFilters();
    if (KeyVisao && KeyVisao == FonteDados) setFiltro([]);
    else {
      let dataFilter = {
        ...dataFilterHeader,
        Filtros: dataFilterHeader.Filtros.filter(x => x.Campo != FonteDados),
      };
      setDataFilterHeader(dataFilter);
    }
    setValorChave('');
  };

  const verificaValor = () => {
    let valor = selectedKeys[0] || '';

    // Para colunas "normais", se o usuário não está digitando,
    // mostra o valor realmente aplicado (pode ter sido removido via painel externo).
    if (!isKeyVisao && !campoAlterado) {
      return obterValorAplicado();
    }

    if (isKeyVisao) {
      if (valor == valorChave && campoAlterado) {
        setCampoAlterado(false);
      } else if (
        filtro?.length > 0 &&
        filtro[0].Campo == KeyVisao &&
        filtro[0].Valor != valorChave &&
        !campoAlterado
      ) {
        valor = filtro[0].Valor;
        setValorChave(valor);
      } else {
        valor =
          (valor != valorChave || valorChave) && campoAlterado
            ? valor
            : valorChave;
      }
    }
    return valor;
  };

  return (
    <div
      style={{
        padding: 8,
      }}
      onKeyDown={e => e.stopPropagation()}
    >
      <div
        style={{
          marginBottom: 8,
          fontSize: 12,
          lineHeight: '16px',
          color: 'rgba(0, 0, 0, 0.65)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <strong>Tipo do campo:</strong> <span>{obterTipoLabelFiltro(TipoValor)}</span>
          <Tooltip
            title={obterDicaTipoFiltro(TipoValor)}
            overlayInnerStyle={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: '#fff',
            }}
          >
            <InfoCircleOutlined
              aria-label="Informação sobre tipo do campo"
              style={{ color: AA_TEXT_ON_LIGHT, cursor: 'help' }}
            />
          </Tooltip>
        </div>
      </div>
      <label
        htmlFor={`filtro-coluna-${FonteDados}`}
        style={{
          display: 'block',
          marginBottom: 4,
          fontSize: 12,
          fontWeight: 600,
          color: AA_TEXT_ON_LIGHT,
        }}
      >
        {`Buscar ${NomeColuna}`}
      </label>
      <Input
        id={`filtro-coluna-${FonteDados}`}
        ref={searchInput}
        placeholder={`Buscar ${NomeColuna}`}
        aria-label={`Buscar ${NomeColuna}`}
        value={verificaValor()}
        onChange={e => {
          setCampoAlterado(true);
          setSelectedKeys(e.target.value ? [e.target.value] : ['']);
        }}
        onPressEnter={e => {
          handleSearch(
            [e.target?.value || ''],
            confirm,
            e.target?.value || ''
          );
        }}
        style={{
          marginBottom: 8,
          display: 'block',
        }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => {
            const v = verificaValor();
            handleSearch([v], confirm, v);
          }}
          icon={<SearchOutlined />}
          size="small"
          style={{
            width: 90,
          }}
        >
          Filtrar
        </Button>
        <Button
          onClick={() => clearFilters && handleReset(clearFilters)}
          size="small"
          style={{
            width: 90,
          }}
        >
          Limpar
        </Button>
      </Space>
    </div>
  );
};

export default FilterHeaderTable;
